/**
 * Step 2 — User Access [TYPE B — Optional]
 *
 * Shows empty state by default. "Set Up User Access Now" replaces it with
 * the full TeamMembersPage — the admin can click any employee and use the
 * "Company Access" tab in the TeamMemberDetailsDialog to assign that person
 * to one or more companies, exactly as they would from the normal app.
 *
 * Guard: if no companies exist yet, shows a dependency notice instead of
 * the "Set Up Now" button — there's nothing to assign to.
 *
 * The PageHeader and stats cards that TeamMembersPage renders at the top
 * are hidden via a useRef+useEffect DOM approach — no CSS selector fragility.
 */

import { useState, useRef, useEffect } from 'react';
import { Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WizardBottomBar } from '@/components/onboarding/WizardBottomBar';
import { TeamMembersPage } from '@/components/Pages/TeamMembersPage';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import type { OnboardingStep } from '@/contexts/OnboardingContext';

const PRIMARY = '#00796B';

interface Step2UserAccessProps {
  onNext: (status: 'completed' | 'skipped') => void;
  onBack: () => void;
}

/**
 * Hides the first N direct children of TeamMembersPage's root div.
 * TeamMembersPage returns: <div class="space-y-6"> → [PageHeader, statsGrid, ...]
 * We hide the first two (header + stats) so the panel starts at the search bar.
 */
function useHideTeamPageChrome(
  containerRef: React.RefObject<HTMLDivElement | null>,
  active: boolean,
  hideCount = 2,
) {
  useEffect(() => {
    if (!active) return;

    const hide = () => {
      const container = containerRef.current;
      if (!container) return;
      const root = container.querySelector<HTMLDivElement>('div.space-y-6');
      if (!root) return;
      const children = Array.from(root.children) as HTMLElement[];
      children.slice(0, hideCount).forEach((el) => {
        el.style.display = 'none';
      });
    };

    hide();
    const t1 = setTimeout(hide, 50);
    const t2 = setTimeout(hide, 200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [active, containerRef, hideCount]);
}

export function Step2UserAccess({ onNext, onBack }: Step2UserAccessProps) {
  const [showEmbedded, setShowEmbedded] = useState(false);
  const { availableCompanies } = useCompany();
  const embedRef = useRef<HTMLDivElement>(null);

  useHideTeamPageChrome(embedRef, showEmbedded);

  const hasCompanies = availableCompanies.length > 0;

  const handleSkip = () => onNext('skipped');
  const handleNext = () => onNext(showEmbedded ? 'completed' : 'skipped');

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Content area — fills all space between top bar and bottom bar */}
      <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-1">
        {!showEmbedded ? (
          /* ── Empty state — centred in available space ── */
          <div className="flex flex-col items-center justify-center h-full text-center max-w-md mx-auto">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: `${PRIMARY}18` }}
            >
              <Users className="w-7 h-7" style={{ color: PRIMARY }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Assign your team to companies
            </h3>
            <p className="text-sm text-gray-500 mb-2 leading-relaxed">
              Control which employees can log time against each company. Click any team member,
              then open the <strong>Company Access</strong> tab to toggle their access on or off
              per company.
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              You can also do this later from <strong>Team → Members</strong> in the main
              navigation.
            </p>

            {hasCompanies ? (
              <Button
                style={{ backgroundColor: PRIMARY, color: 'white' }}
                onClick={() => setShowEmbedded(true)}
              >
                Set Up User Access Now
              </Button>
            ) : (
              <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                Complete Company Setup in Step 1 before assigning user access.
              </p>
            )}
          </div>
        ) : (
          /* ── Embedded TeamMembersPage — fills all available height ── */
          <div className="flex flex-col h-full min-h-0 border border-border rounded-lg bg-white overflow-hidden">
            {/* Sticky tip banner */}
            <div
              className="px-4 py-2.5 border-b border-border text-sm shrink-0"
              style={{ backgroundColor: `${PRIMARY}0D` }}
            >
              <span className="font-semibold" style={{ color: PRIMARY }}>Tip: </span>
              <span className="text-muted-foreground">
                Click any team member below, then select the{' '}
                <strong className="text-gray-700">Company Access</strong> tab to assign them to
                one or more companies.
              </span>
            </div>

            {/* Scrollable employee list — fills remaining space */}
            <div ref={embedRef} className="flex-1 min-h-0 overflow-y-auto p-4">
              <TeamMembersPage />
            </div>
          </div>
        )}
      </div>

      <WizardBottomBar
        currentStep={2 as OnboardingStep}
        helperText={undefined}
        hideBack={false}
        hideSkip={!hasCompanies}
        onBack={onBack}
        onSkip={handleSkip}
        onNext={handleNext}
      />
    </div>
  );
}
